/**
 * Administration — screens 07 (users), 08 (report access) and 10 (companies).
 *
 * These three screens are the only place the answer to "who sees what" is written down,
 * so the rules they enforce live here, beside the two predicates that read them
 * (`contractorScope` in auth.ts, `resolveReportAccess` in grants.ts) rather than inside a
 * dialog's submit handler.
 *
 * ⚠ **The rule that matters most is `roleFitsCompany`.** `contractorScope` returns `null`
 * — meaning *no filter, every row* — for `SYSTEM_ADMIN` and `UNIFIBER_INTERNAL`, and it
 * does so WITHOUT LOOKING AT THE COMPANY. So a person given the Unifiber Internal role
 * while sitting in Circet's company does not see Circet's rows: they see everybody's,
 * including Constructel's. Two dropdowns side by side in a dialog, one wrong click, and
 * the product's central promise is gone with no error anywhere. The API refuses the
 * combination and the screen explains why rather than just greying it out.
 */
import { isInternal } from "./auth.js";
export const COMPANY_KINDS = ["INTERNAL", "CONTRACTOR"];
// ─────────────────────────────────────────────────────────────────────────────
// The role/company rule
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Is this role coherent inside a company of this kind?
 *
 * Both directions are refused, for different reasons:
 *
 *   · An INTERNAL role in a CONTRACTOR company is a **leak**. It is unscoped, so that
 *     person sees every contractor's backlog from inside one of them.
 *   · An EXTERNAL_OPERATOR in the INTERNAL company is **useless**: Unifiber holds no DMP
 *     contractor codes, so `matchesScope` matches nothing and they see an empty report
 *     that looks broken rather than empty-by-design.
 */
export function roleFitsCompany(role, kind) {
    return isInternal(role) ? kind === "INTERNAL" : kind === "CONTRACTOR";
}
/** Why the pairing was refused, in the words the screen shows. */
export function roleCompanyProblem(role, kind) {
    if (roleFitsCompany(role, kind))
        return null;
    return isInternal(role)
        ? "An internal role is never row-scoped, so inside a contractor's company it would see every contractor's rows, not that company's. Use External Operator, or move them to Unifiber."
        : "External Operator scopes a person to their company's DMP contractor codes, and Unifiber has none — they would open the report and find it empty. Use Unifiber Internal or Administrator.";
}
/**
 * ⚠ The account has a password path and no second factor yet.
 *
 * Not "has never signed in": somebody can sign in, be forced through the password change,
 * and stop before scanning the QR code. Until the authenticator exists they cannot reach a
 * report, so the screen counts them as still to finish rather than as a working account.
 */
export function isPendingEnrolment(user) {
    return user.authType !== "ENTRA_ID" && !user.totpEnabled;
}
export function filterUsers(users, filters) {
    const needle = filters.search?.trim().toLowerCase();
    return users.filter((user) => {
        if (filters.companyId && user.companyId !== filters.companyId)
            return false;
        if (filters.status === "active" && !user.isActive)
            return false;
        if (filters.status === "disabled" && user.isActive)
            return false;
        if (filters.auth === "entra" && user.authType === "LOCAL")
            return false;
        // ⚠ BOTH counts as a password account here. It is one, and the question this filter
        // answers is "who could sign in with a password", which is a security question.
        if (filters.auth === "local" && user.authType === "ENTRA_ID")
            return false;
        if (needle) {
            const haystack = `${user.displayName} ${user.email}`.toLowerCase();
            if (!haystack.includes(needle))
                return false;
        }
        return true;
    });
}
// ─────────────────────────────────────────────────────────────────────────────
// Companies — screen 10
// ─────────────────────────────────────────────────────────────────────────────
/**
 * The colours a company may be drawn in.
 *
 * The first three are the brand gradient's own stops, so the common case looks like
 * Unifiber rather than like a chart library. Two companies may not share one: the whole
 * point of a per-company colour is telling them apart on a stacked bar.
 */
export const COMPANY_PALETTE = [
    "#84257a",
    "#ed125f",
    "#f47b3b",
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#0ea5e9",
    // ⚠ Doubled on 28 Sep 2026, beyond mockup 10's eight. Colours are unique per company, so
    // the palette's length IS the most companies the screen can create — and with three
    // companies live, eight left room for five of DMP's eleven homes-passed partners.
    "#1e3a8a",
    "#0f766e",
    "#65a30d",
    "#a16207",
    "#64748b",
    "#be123c",
    "#c026d3",
    "#57534e",
];
/** Lower-case, no spaces — it appears in exports and links. */
export function isValidCompanyCode(code) {
    return /^[a-z0-9][a-z0-9-]{1,30}$/.test(code);
}
/** `CIF - Verbraeken` → `cif-verbraeken`. A suggestion only; the administrator can change it. */
export function suggestCompanyCode(name) {
    return stripAccents(name)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 31)
        .replace(/-+$/, "");
}
function stripAccents(value) {
    return value.normalize("NFD").replace(/[̀-ͯ]/g, "");
}
/**
 * A spelling-insensitive key: `CIF - Circet` and `CIF-Circet` are one company.
 *
 * ⚠ Used ONLY to SUGGEST labels and to spot a partner that already has a company. Scoping
 * itself matches the board's exact string (hard rule 29), so the suggestion puts every real
 * spelling into the company, and never this key.
 */
export function labelKey(label) {
    return stripAccents(label).toLowerCase().replace(/[^a-z0-9]/g, "");
}
/**
 * The Monday labels a DMP partner goes by, spelled the way the boards spell it.
 *
 * `CIF - Circet` finds both `CIF - Circet` (requests) and `CIF-Circet` (uploads) — the
 * reason `mondayCompanyLabels` is an array at all. A partner on neither board yet gets its
 * DMP name, which the dialog then flags as not on the boards.
 */
export function mondayLabelsForPartner(partner, boardLabels) {
    const key = labelKey(partner);
    const matches = boardLabels.filter((label) => labelKey(label) === key);
    return matches.length > 0 ? [...new Set(matches)].sort() : [partner.trim()];
}
/**
 * The company that already stands for this partner, if any.
 *
 * Matched on its Monday labels — `CIF - Circet` is the company called Circet — or on its
 * name, because DMP lists `Unifiber` as a partner and the internal company carries no
 * labels at all. Offering it would only end in "that code is already taken".
 */
export function companyForPartner(partner, companies) {
    const key = labelKey(partner);
    return (companies.find((c) => labelKey(c.name) === key || c.mondayCompanyLabels.some((label) => labelKey(label) === key)) ?? null);
}
/**
 * Why a company's scope is refused, or `null` when it is coherent.
 *
 * ⚠ A contractor needs AT LEAST ONE of the two scopes, not specifically a DMP code. With
 * neither, `contractorScope` and `mondayScope` both answer "no rows", which on every screen
 * looks exactly like a quiet morning (hard rule 28). Requiring a DMP code, as the first
 * version did, made a homes-passed partner impossible to create — its name is on no order.
 */
export function companyScopeProblem(input) {
    if (input.kind === "INTERNAL") {
        if (input.dmpContractorCodes.length > 0) {
            return "An internal company is never row-scoped, so DMP contractor codes would do nothing.";
        }
        if (input.mondayCompanyLabels.length > 0) {
            return "An internal company is never row-scoped, so Monday company labels would do nothing.";
        }
        return null;
    }
    if (input.dmpContractorCodes.length === 0 && input.mondayCompanyLabels.length === 0) {
        return "A contractor needs a DMP contractor code (Order Activation), a Monday company label (Cocon Requests and Uploads), or both — with neither, its people would open every report and find it empty.";
    }
    return null;
}
export function companyImpact(company) {
    return {
        users: company.activeUsers,
        rows: company.rows ?? 0,
        pops: company.pops ?? 0,
        reportGrants: company.reportGrants,
        // ⚠ Counts EVERY user, not just active ones: a disabled account still points at this
        // company, and the foreign key is `onDelete: Restrict`. Offering a delete that the
        // database will refuse is worse than not offering it.
        removable: company.users === 0 && (company.rows ?? 0) === 0,
    };
}
//# sourceMappingURL=admin.js.map